import { useCallback, useMemo, useState } from "react"
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ClipboardList, Download, Lock, Shield, Trash2 } from "lucide-react"
import {
  CollectionView,
  type CollectionConfig,
} from "@/components/collection/CollectionView"
import type { ColumnSpec } from "@/lib/column-layout"
import {
  parseRouteFocus,
  RoutedCollectionLayout,
  useRouteRecordSelection,
} from "@/lib/routed-collection-layout"
import { ConfirmDialog } from "@/components/tickets/ConfirmDialog"
import { FormBuilder } from "@/components/forms/FormBuilder"
import { FormRecordHeader } from "@/components/forms/FormRecordHeader"
import { FormResponsesCollection } from "@/components/forms/FormResponsesCollection"
import { ResponseDetailColumn } from "@/components/forms/ResponseDetailColumn"
import { downloadMappedCsv } from "@/lib/csv"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import {
  audienceLabel,
  deleteForm,
  formQueryOptions,
  formsKey,
  formSetupQueryOptions,
  formsQueryOptions,
  isForm,
  questionCount,
  type FormRow,
} from "@/core/forms"

// The Forms LAYOUT route. Route DEPTH = Main columns:
//   /forms                       → col 1 (forms list)
//   /forms/$formId               → col 1 + 2 (list + form record)
//   /forms/$formId/$responseId   → col 1 + 2 + 3 (+ response detail)
// `$formId`/`$responseId` live on MARKER child routes (component: null); this
// layout reads them off the leaf match and renders ALL columns via ColumnLayout
// (which decides how many fit side-by-side). `?focus=1` forces the single
// deepest column. Legacy `?formId=…` URLs redirect into the nested path.
export const Route = createFileRoute("/$orgSlug/$eventSlug/forms")({
  validateSearch: (
    s: Record<string, unknown>,
  ): { edit?: string; focus?: number; formId?: string } => {
    return {
      edit: s.edit === "new" || typeof s.edit === "string" ? String(s.edit) : undefined,
      focus: parseRouteFocus(s.focus),
      // Legacy — accepted only so beforeLoad can redirect it into the path.
      formId: typeof s.formId === "string" ? s.formId : undefined,
    }
  },
  beforeLoad: ({ params, search }) => {
    if (search.formId) {
      throw redirect({
        to: "/$orgSlug/$eventSlug/forms/$formId",
        params: { ...params, formId: search.formId },
        search: { focus: search.focus, edit: search.edit },
      })
    }
  },
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(formsQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: FormsView,
})

function FormsView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/forms" })
  const { edit, focus } = Route.useSearch()
  const navigate = useNavigate()
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()

  const { formId: selectedId, responseId } = useRouteRecordSelection("formId", "responseId")

  const formsQ = useQuery({ ...formsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const setupQ = useQuery({ ...formSetupQueryOptions(eventId ?? ""), enabled: !!eventId })

  const forms = useMemo(() => (formsQ.data ?? []).filter(isForm), [formsQ.data])
  const groupNameById = useMemo(
    () => new Map((setupQ.data?.groups ?? []).map((g) => [g.id, g.name])),
    [setupQ.data],
  )
  const ticketNameById = useMemo(() => new Map<string, string>(), [])

  // Navigation helpers — drilling/closing CLEARS any fullscreen focus (you want
  // to see the thing you just opened, in context).
  const openForm = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/forms/$formId",
        params: { ...params, formId: id },
        search: {},
      }),
    [navigate, params],
  )
  const closeForm = useCallback(
    () => void navigate({ to: "/$orgSlug/$eventSlug/forms", params, search: {} }),
    [navigate, params],
  )
  const openResponse = useCallback(
    (guestId: string) => {
      if (!selectedId) return
      void navigate({
        to: "/$orgSlug/$eventSlug/forms/$formId/$responseId",
        params: { ...params, formId: selectedId, responseId: guestId },
        search: {},
      })
    },
    [navigate, params, selectedId],
  )
  const closeResponse = useCallback(() => {
    if (!selectedId) return
    void navigate({
      to: "/$orgSlug/$eventSlug/forms/$formId",
      params: { ...params, formId: selectedId },
      search: {},
    })
  }, [navigate, params, selectedId])
  // Fullscreen a specific COLUMN (1=list, 2=form, 3=response). Toggles off if
  // that column is already focused. Stays on the current route depth.
  const toggleFocus = useCallback(
    (n: number) => {
      const next = focus === n ? undefined : n
      if (responseId && selectedId) {
        void navigate({
          to: "/$orgSlug/$eventSlug/forms/$formId/$responseId",
          params: { ...params, formId: selectedId, responseId },
          search: { focus: next, edit },
        })
      } else if (selectedId) {
        void navigate({
          to: "/$orgSlug/$eventSlug/forms/$formId",
          params: { ...params, formId: selectedId },
          search: { focus: next, edit },
        })
      } else {
        void navigate({ to: "/$orgSlug/$eventSlug/forms", params, search: { focus: next, edit } })
      }
    },
    [navigate, params, selectedId, responseId, focus, edit],
  )
  const openBuilder = useCallback(
    (id?: string) => {
      if (id) {
        void navigate({
          to: "/$orgSlug/$eventSlug/forms/$formId",
          params: { ...params, formId: id },
          search: { focus, edit: id },
        })
      } else {
        void navigate({
          to: "/$orgSlug/$eventSlug/forms",
          params,
          search: { focus, edit: "new" },
        })
      }
    },
    [navigate, params, focus],
  )
  const closeBuilder = useCallback(() => {
    if (edit && edit !== "new") {
      void navigate({
        to: "/$orgSlug/$eventSlug/forms/$formId",
        params: { ...params, formId: edit },
        search: { focus },
      })
    } else {
      void navigate({ to: "/$orgSlug/$eventSlug/forms", params, search: { focus } })
    }
  }, [navigate, params, edit, focus])
  const onBuilderSaved = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/forms/$formId",
        params: { ...params, formId: id },
        search: { focus },
      }),
    [navigate, params, focus],
  )

  const [confirmDelete, setConfirmDelete] = useState<FormRow | null>(null)
  const del = useMutation({
    mutationFn: (id: string) => deleteForm(id),
    onSuccess: (_r, id) => {
      void qc.invalidateQueries({ queryKey: formsKey(eventId!) })
      setConfirmDelete(null)
      if (selectedId === id) closeForm()
      toast.success("Form deleted")
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  })

  const exportCsv = useCallback(
    (rows: FormRow[]) => {
      downloadMappedCsv(
        "forms.csv",
        ["Title", "Audience", "Questions", "Emphasis", "Visibility"],
        rows,
        (r) => [
          r.title,
          audienceLabel(r, groupNameById, ticketNameById),
          questionCount(r),
          r.flavor === "important" ? "Important" : "Standard",
          r.responseVisibility === "private" ? "Private" : "Visible",
        ],
      )
    },
    [groupNameById, ticketNameById],
  )

  const config = useMemo<CollectionConfig<FormRow>>(
    () => ({
      title: "Forms",
      getId: (r) => r.id,
      searchText: (r) => `${r.title} ${audienceLabel(r, groupNameById, ticketNameById)}`,
      onOpen: (r) => openForm(r.id),
      // Right-click → Open in preview (onOpen) / Fullscreen (focus the form
      // column) / new tab+window (via the href).
      getRecordHref: (r) => `/${params.orgSlug}/${params.eventSlug}/forms/${r.id}`,
      onOpenFullscreen: (r) =>
        void navigate({
          to: "/$orgSlug/$eventSlug/forms/$formId",
          params: { ...params, formId: r.id },
          search: { focus: 2 },
        }),
      onNew: () => openBuilder(),
      newLabel: "New form",
      defaultSortId: "title",
      defaultSortDir: "asc",
      emptyTitle: "No forms",
      emptyBlurb:
        "Forms collect structured answers from attendees — waivers, t-shirt sizes, dietary needs, emergency contacts.",
      emptyIcon: <ClipboardList className="h-5 w-5" />,
      savedViews: [
        { id: "all", label: "All", filter: () => true },
        { id: "important", label: "Important", filter: (r) => r.flavor === "important" },
        {
          id: "private",
          label: "Private",
          filter: (r) => r.responseVisibility === "private",
        },
      ],
      filters: [
        {
          id: "audience",
          label: "Audience",
          options: [
            ["all", "All"],
            ["event", "All attendees"],
            ["activity", "Activity attendees"],
            ["segment", "Segment"],
            ["person", "Specific attendees"],
          ],
          predicate: (v, r) => r.audience === v,
        },
      ],
      groupOptions: [
        {
          id: "audience",
          label: "Audience",
          groupOf: (r) => audienceLabel(r, groupNameById, ticketNameById),
        },
        {
          id: "emphasis",
          label: "Emphasis",
          groupOf: (r) => (r.flavor === "important" ? "Important" : "Standard"),
          order: (l) => (l === "Important" ? 0 : 1),
        },
      ],
      bulkActions: [
        {
          id: "export",
          label: "Export",
          icon: <Download className="h-4 w-4" />,
          run: exportCsv,
        },
      ],
      // No ⋮ on the list action bar — New lives in the TitleBar and Export is a
      // bulk action; the menu just duplicated them.
      columns: [
        {
          id: "title",
          header: "Name",
          sortable: true,
          sortValue: (r) => r.title.toLowerCase(),
          grow: true,
          truncate: true,
          render: (r) => (
            <div className="flex min-w-0 items-center gap-2.5">
              <ClipboardList className="h-4 w-4 shrink-0 text-plum-700/55 dark:text-plum-200/45" />
              <span className="truncate font-semibold text-plum-900 dark:text-plum-50">
                {r.title || "Untitled form"}
              </span>
              {r.systemKey && (
                <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-plum-600/10 px-1.5 py-0.5 text-[10px] font-semibold text-plum-700 dark:bg-plum-400/15 dark:text-plum-200">
                  Official
                </span>
              )}
              {r.flavor === "important" && (
                <Shield className="h-3.5 w-3.5 shrink-0 text-rose-500 dark:text-rose-400" />
              )}
              {r.responseVisibility === "private" && (
                <Lock className="h-3.5 w-3.5 shrink-0 text-plum-700/45 dark:text-plum-200/35" />
              )}
            </div>
          ),
        },
        {
          id: "audience",
          header: "Audience",
          className: "text-plum-700/75 dark:text-plum-200/60",
          sortable: true,
          sortValue: (r) => audienceLabel(r, groupNameById, ticketNameById).toLowerCase(),
          render: (r) => audienceLabel(r, groupNameById, ticketNameById),
        },
        {
          id: "questions",
          header: "Questions",
          align: "center",
          sortable: true,
          sortValue: (r) => questionCount(r),
          render: (r) => (
            <span className="tabular-nums text-plum-700/70 dark:text-plum-200/55">
              {questionCount(r)}
            </span>
          ),
        },
        {
          id: "status",
          header: "Status",
          render: (r) =>
            r.responseVisibility === "private" ? (
              <span className="rounded-md bg-plum-100 px-2 py-0.5 text-[11px] font-semibold text-plum-700/60 dark:bg-white/10 dark:text-plum-200/45">
                Private
              </span>
            ) : (
              <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
                Live
              </span>
            ),
        },
      ],
    }),
    [groupNameById, ticketNameById, openForm, openBuilder, exportCsv, params, navigate],
  )

  // Builder takes over the canvas — no inline form pane underneath it.
  const showRecord = !!selectedId && !edit

  const columns = useMemo<ColumnSpec[]>(() => {
    const cols: ColumnSpec[] = [
      {
        id: "list",
        minWidth: 320,
        width: 380,
        node: (
          <CollectionView
            config={config}
            rows={forms}
            activeId={showRecord ? selectedId : null}
            loading={formsQ.isLoading && forms.length === 0}
            error={
              formsQ.isError
                ? ((formsQ.error as Error)?.message ?? "Failed to load forms")
                : null
            }
          />
        ),
      },
    ]
    if (showRecord && selectedId && eventId) {
      cols.push({
        id: "form",
        minWidth: 360,
        fill: true,
        node: (
          <FormRecordPane
            eventId={eventId}
            formId={selectedId}
            orgSlug={params.orgSlug}
            eventSlug={params.eventSlug}
            groupNameById={groupNameById}
            ticketNameById={ticketNameById}
            selectedResponseId={responseId}
            onSelectResponse={openResponse}
            onEdit={() => openBuilder(selectedId)}
            onDelete={(row) => setConfirmDelete(row)}
            onClose={closeForm}
            onFullscreen={() => toggleFocus(2)}
            responseHref={(guestId) =>
              `/${params.orgSlug}/${params.eventSlug}/forms/${selectedId}/${guestId}`
            }
            onFullscreenResponse={(guestId) =>
              void navigate({
                to: "/$orgSlug/$eventSlug/forms/$formId/$responseId",
                params: { ...params, formId: selectedId, responseId: guestId },
                search: { focus: 3 },
              })
            }
          />
        ),
      })
      if (responseId) {
        cols.push({
          id: "response",
          minWidth: 440,
          width: 440,
          node: (
            <ResponseDetailColumn
              eventId={eventId}
              formId={selectedId}
              responseId={responseId}
              onClose={closeResponse}
            />
          ),
        })
      }
    }
    return cols
  }, [
    config,
    forms,
    formsQ.isLoading,
    formsQ.isError,
    formsQ.error,
    showRecord,
    selectedId,
    responseId,
    eventId,
    params.orgSlug,
    params.eventSlug,
    groupNameById,
    ticketNameById,
    openResponse,
    openBuilder,
    closeForm,
    closeResponse,
    toggleFocus,
  ])

  return (
    <RoutedCollectionLayout columns={columns} focus={focus} className="relative h-full min-w-0">
      {/* Builder — contained overlay (not a body portal). */}
      {edit && eventId && (
        <BuilderOverlay
          eventId={eventId}
          formId={edit === "new" ? null : edit}
          onClose={closeBuilder}
          onSaved={onBuilderSaved}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={`Delete "${confirmDelete?.title ?? "form"}"?`}
        danger
        icon={<Trash2 className="h-[18px] w-[18px]" />}
        body={
          <>
            This permanently removes the form and all of its responses. Attendees
            will no longer see it.
            {confirmDelete?.systemKey && (
              <span className="mt-2 block font-semibold text-rose-600 dark:text-rose-400">
                This is the official “{confirmDelete.systemKey}” form — deleting it
                stops feeding the first-class {confirmDelete.systemKey} data for this
                event.
              </span>
            )}
          </>
        }
        confirmLabel={del.isPending ? "Deleting…" : "Delete form"}
        busy={del.isPending}
        onConfirm={() => confirmDelete && del.mutate(confirmDelete.id)}
      />
    </RoutedCollectionLayout>
  )
}

// Loads the existing row (when editing) before mounting the builder so the
// fields hydrate. New forms mount immediately with a blank initial state.
function BuilderOverlay({
  eventId,
  formId,
  onClose,
  onSaved,
}: {
  eventId: string
  formId: string | null
  onClose: () => void
  onSaved: (id: string) => void
}) {
  const existing = useQuery({
    ...formQueryOptions(formId ?? ""),
    enabled: !!formId,
  })

  if (formId && existing.isLoading) {
    // Mirror the FormBuilder shell so the header bar + content column don't
    // shift in when the form loads: same overlay container, a header row with
    // a title + trailing-action cluster, and the max-w-2xl p-5 body column.
    return (
      <div className="absolute inset-0 z-40 flex flex-col bg-surface">
        <div className="flex items-center gap-2 border-b border-plum-200/60 px-5 py-3 dark:border-white/10">
          <div className="h-5 w-28 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
          <div className="ml-auto flex items-center gap-2">
            <div className="h-8 w-16 animate-pulse rounded-lg bg-plum-100 dark:bg-white/10" />
            <div className="h-8 w-20 animate-pulse rounded-lg bg-plum-100 dark:bg-white/10" />
            <div className="h-7 w-7 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          <div className="mx-auto max-w-2xl space-y-6 p-5">
            <div className="h-24 animate-pulse rounded-xl bg-plum-100 dark:bg-white/10" />
            <div className="h-40 animate-pulse rounded-xl bg-plum-100 dark:bg-white/10" />
            <div className="h-40 animate-pulse rounded-xl bg-plum-100 dark:bg-white/10" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <FormBuilder
      eventId={eventId}
      formId={formId}
      initial={existing.data ?? null}
      onClose={onClose}
      onSaved={onSaved}
    />
  )
}

// Column 2: the form-record header + responses table. Selecting a response
// promotes it to column 3 (rendered by the layout, outside this pane).
function FormRecordPane({
  eventId,
  formId,
  orgSlug,
  eventSlug,
  groupNameById,
  ticketNameById,
  onEdit,
  onDelete,
  onClose,
  onFullscreen,
  selectedResponseId,
  onSelectResponse,
  responseHref,
  onFullscreenResponse,
}: {
  eventId: string
  formId: string
  orgSlug: string
  eventSlug: string
  groupNameById: Map<string, string>
  ticketNameById: Map<string, string>
  onEdit: () => void
  onDelete: (row: FormRow) => void
  onClose: () => void
  onFullscreen: () => void
  selectedResponseId: string | null
  onSelectResponse: (guestId: string) => void
  responseHref: (guestId: string) => string
  onFullscreenResponse: (guestId: string) => void
}) {
  const q = useQuery({ ...formQueryOptions(formId), enabled: !!formId })
  const form = q.data

  return (
    <div className="flex h-full flex-col bg-surface">
      <FormRecordHeader
        form={form}
        groupNameById={groupNameById}
        ticketNameById={ticketNameById}
        orgSlug={orgSlug}
        eventSlug={eventSlug}
        formId={formId}
        onEdit={onEdit}
        onDelete={() => form && onDelete(form)}
        onClose={onClose}
        onFullscreen={onFullscreen}
      />
      <div className="min-h-0 flex-1">
        <FormResponsesCollection
          eventId={eventId}
          formId={formId}
          selectedResponseId={selectedResponseId}
          onSelectResponse={onSelectResponse}
          recordHref={responseHref}
          onFullscreenResponse={onFullscreenResponse}
        />
      </div>
    </div>
  )
}
